import React, { useState, useEffect } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import styled from "styled-components";
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import subscriptionPlans from './subscriptionPlans';
import useTracking from '../../hooks/useTracking';
import PaymentService from '../../services/paymentService';
import ChariowPhoneModal from '../Payment/ChariowPhoneModal';

const StyledOffcanvas = styled(Offcanvas)`
  max-height: 100vh !important;
  height: 100vh !important;
  border-radius: 0 !important;
  background: ${props => `linear-gradient(180deg, 
    ${getPlanPrimaryColor(props.plantype)} -80%, 
    #ffffff 100%)`} !important;

  @media (max-width: 768px) {
    min-height: 100vh !important;
    height: -webkit-fill-available !important;
    width: 100vw !important;
    margin: 0 !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    
    .offcanvas-body {
      padding: 0 !important;
      height: 100% !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch !important;
      display: flex !important;
      flex-direction: column !important;
    }
  }
`;

const OffcanvasContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 180px;
  touch-action: pan-y;
  overscroll-behavior: contain;
`;

const FixedBottomButton = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  z-index: 1000;

  @supports (padding-bottom: env(safe-area-inset-bottom)) {
    padding-bottom: calc(15px + env(safe-area-inset-bottom));
  }
`;

const getPlanGradient = (plantype) => {
  switch (plantype) {
    case 'plus':
      return 'linear-gradient(45deg, #FF50A2 0%, #FF7676 100%)';
    case 'gold':
      return 'linear-gradient(45deg, #FFD700, #FFA500)';
    case 'platinum':
      return 'linear-gradient(45deg, #E5E4E2 0%, #B4B4B4 100%)';
    default:
      return 'linear-gradient(45deg, #FF50A2 0%, #FF7676 100%)';
  }
};

const getPlanPrimaryColor = (plantype) => {
  switch (plantype) {
    case 'plus':
      return '#ffa8d1';
    case 'gold':
      return '#FFD700';
    case 'platinum':
      return '#666666';
    default:
      return '#FF50A2';
  }
};

const formatPrice = (price) => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const calculateDiscount = (basePrice, actualPrice, duration) => {
  const totalBasePrice = basePrice * duration;
  const discount = ((totalBasePrice - actualPrice) / totalBasePrice) * 100;
  return Math.round(discount);
};

const Header = styled.div`
  padding: 20px;
  text-align: center;
  margin: -1px -1px 20px -1px;
  border-radius: 30px 30px 0 0;

  
  .col-12 {
    padding: 20px;
    
    .logo-img {
      position: relative;
      margin-bottom: 15px;
      
      img {
        height: 40px;
        width: auto;
        margin-bottom: 10px;
      }
      
      .badge {
        display: inline-block;
        padding: 5px 15px;
        background: ${props => getPlanGradient(props.plantype)};
        border-radius: 20px;
        color: white;
        font-weight: 600;
        font-size: 14px;
      }
    }
    
    p {
      color: #000;
      margin: 0;
      font-size: 16px;
      opacity: 0.9;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  left: 20px;
  top: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  z-index: 1;
`;

const SubscriptionOptions = styled.div`
  display: flex;
  padding: 0 20px;
  gap: 10px;
  margin-bottom: 20px;
`;

const SubscriptionCard = styled.div`
  flex: 1;
  padding: 15px;
  border-radius: 12px;
  border: 2px solid ${props => props.selected ? (
    props.plantype === 'plus' ? '#FF50A2' :
    props.plantype === 'gold' ? '#FFD700' :
    props.plantype === 'platinum' ? '#E5E4E2' : '#e8e8e8'
  ) : '#e8e8e8'};
  cursor: pointer;
  background: ${props => props.selected ? 'white' : '#fff'};
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    font-size: 14px;
    margin-bottom: 5px;
    font-weight: 600;
    background: ${props => props.selected ? getPlanGradient(props.plantype) : 'transparent'};
    -webkit-background-clip: ${props => props.selected ? 'text' : 'none'};
    -webkit-text-fill-color: ${props => props.selected ? 'transparent' : 'inherit'};
  }
  
  .price {
    font-size: 20px;
    color: #000;
    margin-bottom: 5px;
    font-weight: ${props => props.selected ? '600' : '400'};
  }
  
  .savings {
    color: #000;
    font-size: 14px;
    font-weight: 500;
  }
  
  .checkmark {
    position: absolute;
    top: 10px;
    right: 10px;
    color: ${props => 
      props.plantype === 'plus' ? '#FF50A2' :
      props.plantype === 'gold' ? '#FFD700' :
      props.plantype === 'platinum' ? '#E5E4E2' : '#000'
    };
    display: ${props => props.selected ? 'block' : 'none'};
  }
`;

const FeaturesList = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  margin: 0 20px;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: ${props => props.included === "true" ? "#000" : "#666"};
  
  svg {
    margin-right: 15px;
    min-width: 24px;
    color: ${props => props.included === "true" ? getPlanPrimaryColor(props.plantype) : "#666"};
  }
  
  .feature-text {
    h4 {
      margin: 0;
    font-size: 14px;
      font-weight: ${props => props.included === "true" ? "600" : "400"};
    }
  }
`;

const ContinueButton = styled.button`
  width: calc(100% - 40px);
  margin-inline: 20px;
  padding: 15px;
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  background: ${props => getPlanGradient(props.plantype)};
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FullScreenSpinner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid ${props => getPlanPrimaryColor(props.plantype)};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SubscriptionOffcanvas = ({ show, onClose, planType, title }) => {
  const tracking = useTracking();
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const authState = useSelector(state => state.auth);
  const savedPhoneNumber = authState.numero || authState.phoneNumber || '';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const currentPlan = subscriptionPlans[planType];
  const prices = currentPlan.prices;

  const handleSubscribe = () => {
    setShowPhoneModal(true);
  };

  const submitSubscription = async (paymentPhoneNumber) => {
    try {
      setIsLoading(true);
      const selectedPrice = prices.find(p => p.duration === selectedDuration);

      // Initier le paiement via Chariow
      const result = await PaymentService.initiateChariowPayment(
        selectedPrice.id,
        paymentPhoneNumber,
        tracking.getAttributionData()
      );

      if (result.step === 'payment' && result.paymentUrl) {
        // Rediriger l'utilisateur vers la page de paiement Chariow
        tracking.trackAction('subscription_initiated', {
          plan: currentPlan.name,
          duration: selectedDuration,
          price: selectedPrice.price,
          provider: 'chariow'
        });
        window.location.href = result.paymentUrl;
      } else if (result.step === 'completed') {
        // Produit gratuit ou déjà activé immédiatement
        tracking.trackAction('subscription_completed', {
          plan: currentPlan.name,
          provider: 'chariow'
        });
        window.location.reload();
      } else if (result.step === 'already_purchased') {
        // L'utilisateur possède déjà ce plan
        console.info('Plan déjà acheté:', selectedPrice.id);
      }
    } catch (error) {
      console.error('Erreur lors de l\'initiation du paiement Chariow:', error);
    } finally {
      setIsLoading(false);
      setShowPhoneModal(false);
    }
  };

  return (
    <>
      {isLoading && (
        <FullScreenSpinner plantype={planType}>
          <div className="spinner" />
        </FullScreenSpinner>
      )}
      <StyledOffcanvas 
        show={show} 
        onHide={onClose} 
        placement={isMobile ? "end" : "bottom"}
        plantype={planType}
      >
        <Offcanvas.Body>
          <OffcanvasContent>
            <CloseButton onClick={onClose}>✕</CloseButton>
            <Header plantype={planType}>
              <div className="col-12">
                <div className="logo-img">
                  <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="Logo" />
                  <span className="badge">{t(currentPlan.name)}</span>
                </div>
                <p>{title || t(currentPlan.description)}</p>
              </div>
            </Header>
            
            <SubscriptionOptions>
              {prices.map((period) => {
                const basePrice = parseInt(prices.find(p => p.duration === 1).price);
                const currentPrice = parseInt(period.price);
                const discount = period.duration > 1 ? calculateDiscount(basePrice, currentPrice, period.duration) : 0;
                
                return (
                  <SubscriptionCard
                    key={period.duration}
                    selected={selectedDuration === period.duration}
                    plantype={planType}
                    onClick={() => setSelectedDuration(period.duration)}
                  >
                    <div className="checkmark">✓</div>
                    <h3>{t(period.key)}</h3>
                    <div className="price">
                      {formatPrice(period.price)} FCFA
                    </div>
                    {discount > 0 && (
                      <div className="savings">
                        {t('subscription.discount', { percent: discount })}
                      </div>
                    )}
                  </SubscriptionCard>
                );
              })}
            </SubscriptionOptions>

            <FeaturesList>
              {currentPlan.features.map((feature, index) => (
                <Feature 
                  key={index} 
                  included={feature.included.toString()}
                  plantype={planType}
                >
                  <i className={`icon feather icon-${feature.included ? 'check' : 'lock'}`}></i>
                  <span>{t(feature.key)}</span>
                </Feature>
              ))}
            </FeaturesList>
          </OffcanvasContent>

          <FixedBottomButton>
            <ContinueButton onClick={handleSubscribe} plantype={planType}>
              {t('subscription.continue')} - {formatPrice(prices.find(p => p.duration === selectedDuration).price)} FCFA
            </ContinueButton>
          </FixedBottomButton>
        </Offcanvas.Body>
      </StyledOffcanvas>
      <ChariowPhoneModal
        show={showPhoneModal}
        defaultPhoneNumber={savedPhoneNumber}
        loading={isLoading}
        onCancel={() => !isLoading && setShowPhoneModal(false)}
        onConfirm={submitSubscription}
      />
    </>
  );
};

export default SubscriptionOffcanvas;
